import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  #pendingAdds = 0;

  #layoutObserver = new ResizeObserver(() => this.#updateStickyState());
  #contentObserver = new MutationObserver(() => this.#updateStickyState());

  /** @type {Element | null} */
  #observedFooter = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    window.addEventListener('resize', this.#handleAdResize);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    if (this.#dialog) this.#layoutObserver.observe(this.#dialog, { box: 'border-box' });
    this.#contentObserver.observe(this, { childList: true, subtree: true });

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    window.removeEventListener('resize', this.#handleAdResize);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#layoutObserver.disconnect();
    this.#contentObserver.disconnect();
    this.#observedFooter = null;
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    // Product-page adds use the drawer as confirmation instead of a button checkmark.
    const isProductPageAdd =
      event.target instanceof Element && event.target.closest('.product-page-section[data-product-page]');
    const sourceQuickView = event.target instanceof Element && event.target.closest('#quick-add-dialog');
    const shouldAutoOpen =
      (this.hasAttribute('auto-open') || isProductPageAdd || Boolean(sourceQuickView)) && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // Give PDP purchases immediate feedback, including the first item in an empty
    // cart. Keep stale contents and checkout hidden until reconciliation finishes.
    const opensWhilePending = Boolean(isProductPageAdd && !sourceModal && event.promise &&
      (shouldAutoOpen || this.#pendingAdds > 0));
    if (opensWhilePending) {
      this.#pendingAdds++;
      this.#themeDrawer?.setAttribute('data-cart-pending', '');
      if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
    } else if (this.hasAttribute('auto-open') && shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    let pendingFinished = false;
    const finishPending = (failed = false) => {
      if (!opensWhilePending || pendingFinished) return;
      pendingFinished = true;
      this.#pendingAdds = Math.max(0, this.#pendingAdds - 1);
      if (this.#pendingAdds > 0) return;
      const loading = this.querySelector('.cart-drawer__loading');
      const restoreFocus = document.activeElement === document.body || loading?.contains(document.activeElement);
      this.#themeDrawer?.removeAttribute('data-cart-pending');
      if (failed) {
        this.#themeDrawer?.close();
      } else if (restoreFocus) {
        // Wait for the confirmed cart morph before replacing the hidden loading
        // control's focus. Respect a shopper who has already moved focus.
        requestAnimationFrame(() => {
          if (!this.#themeDrawer?.isOpen || this.#pendingAdds > 0) return;
          if (document.activeElement !== document.body && !loading?.contains(document.activeElement)) return;
          this.querySelector('.cart-drawer__inner .theme-drawer__close-button')?.focus({ preventScroll: true });
        });
      }
    };

    event.promise
      ?.then(({ detail }) => {
        finishPending(Boolean(detail?.didError));
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        // The user may have dismissed the pending drawer. Do not reopen it.
        if (opensWhilePending) {
          settle();
          return;
        }

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        finishPending(true);
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #handleAdResize = () => {
    if (this.querySelector('.cart-drawer__content--with-ad')) this.#updateStickyState();
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');
    const footer = content && summary ? dialog.querySelector('.cart-drawer__footer') || summary : null;

    // Cart morphs can replace the footer; late font/payment content can resize it
    // after the cart event's first frame. Keep the measured node observed.
    if (footer !== this.#observedFooter) {
      if (this.#observedFooter) this.#layoutObserver.unobserve(this.#observedFooter);
      this.#observedFooter = footer;
      if (footer) this.#layoutObserver.observe(footer, { box: 'border-box' });
    }

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    // Include the optional bottom ad so a tall footer can scroll with the cart.
    if (!footer) return;
    const summaryHeight = footer.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
